import { useContext, useEffect, useState, useCallback, useRef } from 'react';
import { HashRouter, Routes, Route, Navigate, MemoryRouter, Link } from 'react-router-dom';
import { Tooltip } from 'react-tooltip';
import { buttonVariants, ToastAction, Toaster, useToast } from '@playcastdotio/Shadcn';
import { LoginProfile } from '@playcastdotio/CoreTypes';
import { loginProfileState, remoteHostList, steamInstallState, performanceModeState, adModeState, hostState, getSharedStores, hostMatchmakingState, ownerAuthIdState, OWNER_AUTH_ID_STORAGE_KEY, machineIdState, hostPresenceState } from './api/stores/shared';
import { PlaycastFunctions, getFunctions, isElectron } from './api';
import { getElectronStores } from './api/stores/electronStores';
import 'tailwindcss/tailwind.css';
import 'react-tooltip/dist/react-tooltip.css';
import { Chrome } from './components/Chrome';
import { Content } from './components/layout/Content';
import { UpdaterModal } from './components/widgets/UpdaterModal';
import { PlaycastContext, PlaycastWrapper } from './components/PlaycastWrapper';
import { DevData } from './components/dev/DevData';
import { ReleaseDevData } from './components/dev/ReleaseDevData';
import { TesterTools } from './components/dev/TesterTools';
import { Experimental } from './components/dev/Experimental';
import { About } from './pages/About/Index';
import { Studio } from './pages/Studio';
import { USE_BEACON_COORDINATOR, isBeaconMode } from './config/featureFlags';
import BeaconStudio from './pages/Beacon/BeaconStudio';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { faCancel, faCheck, faComputer, faFaceThinking, faSpinner, faWifi, faWifiSlash } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import playcastHolo from '../assets/playcast-holo.png';
import clsx from 'clsx';
import { SignIn, useAuth, useClerk, useSession, useSignIn, useUser } from '@clerk/clerk-react';
import * as Sentry from '@sentry/react';
import constants from '@playcastdotio/GlobalConfig';
import { Inputs } from './pages/Inputs';
import { showToast, ToastContainer } from './components/ui/CustomToast';
import { LobbyV2 } from './pages/LobbyV2';
import { BeaconPlayerControls } from './pages/Beacon/BeaconPlayerControls';
import { Steam } from './pages/Steam/Steam';
import { PavilionPostLogin } from './pages/Steam/PavilionPostLogin';
import { startMatchmakingDiagnosticsMonitoring, sendMatchmakingDiagnostics, burstMatchmakingDiagnostics } from './api/modules/diagnostics/matchmakingDiagnostics';
import { NotificationToastWrapper } from './components/ui/NotificationToast';
import { updateSentryUser } from './api/sentry/machineIdSetup';
import { subscribeMachineIdToSentry, subscribeBroadcastIdToSentry } from './sentry/sentryRenderer';
import { initializeMachineIdMonitor } from './api/sentry/machineIdMonitor';
import { MachineIdGuard } from './components/MachineIdGuard';
import { ClerkFrontendDiagnostics } from './api/clerk/clerkDiagnostics';
import { initializeAnalyticsIdentity, initializeAnalyticsIdentityRemote, cleanupAnalyticsIdentity } from './api/analytics/contextSetup';
import { useAppLaunchedAnalytics } from './hooks/useAppLaunchedAnalytics';
// import { getTwitchBot } from '@playcastdotio/TwitchBot';

// import { useClerkDebug } from './components/ClerkDebug';

// Initializes the bots
// const twitchBot = getTwitchBot();

// ========================================
// Coordinator Initialization
// ========================================
// Coordinators are initialized in the useEffect below via init.ts
// This ensures proper React lifecycle and centralized initialization

/**
 * Creates an anonymous authId by hashing the machineId
 * Must match the server-side implementation exactly
 */
const createAnonymousAuthId = async (machineId: string): Promise<string> => {
  const encoder = new TextEncoder();
  const data = encoder.encode(`anon_${machineId}`);
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
  return `anon_${hashHex.substring(0, 16)}`; // Match server: shortened for readability
};

export default function App() {
  // Initialize coordinators (strangler-fig pattern)
  useEffect(() => {
    // TODO-STRANGLER-FIG: This is early initialization - coordinators are initialized here
    // but will eventually replace the legacy state store system entirely
    import('./coordinators/init').then(({ initializeCoordinators }) => {
      initializeCoordinators();
    }).catch((error) => {
      console.error('[App] Failed to initialize coordinators:', error);
    });
  }, []); // Run once on mount

  // Check if running in guest mode via state store (single source of truth)
  const isGuestMode = hostMatchmakingState.currentState.guestAccount;
  const [functions, setFunctions] = useState<PlaycastFunctions | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Clerk hooks - always called to satisfy React Hooks rules
  // In guest mode, we bypass Clerk logic but keep hooks available
  const { isSignedIn: clerkIsSignedIn, isLoaded: clerkIsLoaded, userId } = useAuth();
  const { session } = useSession();
  const { user } = useUser();
  const { signIn, isLoaded: signInLoaded } = useSignIn();
  const { setActive } = useClerk();
  const isSignedInRef = useRef(clerkIsSignedIn);
  isSignedInRef.current = clerkIsSignedIn;

  // In guest mode, treat as always signed in and loaded
  const isSignedIn = isGuestMode ? true : clerkIsSignedIn;
  const isLoaded = isGuestMode ? true : clerkIsLoaded;

  const [isSteamInstall, setIsSteamInstall] = useState(false);
  const [isPerformanceMode, setIsPerformanceMode] = useState(false);
  // useClerkDebug();

  // Add state to track token refresh interval
  const [tokenRefreshInterval, setTokenRefreshInterval] = useState<NodeJS.Timeout | null>(null);

  // Initialize guest mode auth with placeholder values
  useEffect(() => {
    if (isGuestMode) {
      console.log('[App] Guest mode detected - initializing anonymous auth');

      // If Clerk is already signed in, sign out immediately
      if (clerkIsSignedIn && session) {
        console.warn('[App] Guest mode but Clerk is signed in - signing out to prevent auth conflict');

        // Track this unexpected scenario in Sentry
        Sentry.captureMessage('Guest mode with active Clerk session - forcing logout', {
          level: 'warning',
          tags: {
            issue_type: 'guest_mode_clerk_conflict',
            guest_mode: true,
            clerk_signed_in: clerkIsSignedIn,
          },
          extra: {
            sessionExists: !!session,
            userId: user?.id || 'unknown',
            edition: hostState.currentState.edition,
            guestAccount: hostMatchmakingState.currentState.guestAccount,
          },
        });

        session.end().then(() => {
          console.log('[App] Clerk session ended, reloading to initialize guest mode');
          window.location.reload();
        }).catch((error) => {
          console.error('[App] Error ending Clerk session:', error);

          // Track failure to end session
          Sentry.captureException(error, {
            level: 'error',
            tags: {
              issue_type: 'guest_mode_clerk_signout_failed',
              guest_mode: true,
            },
          });

          // Force reload anyway to try to recover
          window.location.reload();
        });
        return; // Don't initialize yet, will reload after sign out
      }

      // Get machine name and machineId for anonymous auth
      const initializeGuestAuth = async () => {
        const { authState, machineNameState, machineIdState } = getSharedStores();

        // Wait for machineId to be available (required for hashing)
        const machineId = await new Promise<string>((resolve) => {
          const currentId = machineIdState.currentState;

          if (currentId && currentId.length > 0) {
            resolve(currentId);
            return;
          }

          let listener: string | null = null;

          // Timeout after 2 seconds
          const timeout = setTimeout(() => {
            if (listener) {
              machineIdState.removeListener(listener);
            }
            resolve(''); // Fallback if machineId not available
          }, 2000);

          listener = machineIdState.addListener((id) => {
            if (id && id.length > 0) {
              clearTimeout(timeout);
              if (listener) {
                machineIdState.removeListener(listener);
              }
              resolve(id);
            }
          });
        });

        // Wait for machine name to be available (for display purposes)
        const machineName = await new Promise<string>((resolve) => {
          const currentName = machineNameState.currentState;

          if (currentName && currentName.length > 0) {
            resolve(currentName);
            return;
          }

          let listener: string | null = null;

          // Timeout after 2 seconds
          const timeout = setTimeout(() => {
            if (listener) {
              machineNameState.removeListener(listener);
            }
            resolve('Unknown'); // Fallback if machine name not available
          }, 2000);

          listener = machineNameState.addListener((name) => {
            if (name && name.length > 0) {
              clearTimeout(timeout);
              if (listener) {
                machineNameState.removeListener(listener);
              }
              resolve(name);
            }
          });
        });

        // Create anonymous authId using same hashing technique as realtime-API
        // This ensures frontend and backend agree on the authId
        const anonymousAuthId = await createAnonymousAuthId(machineId);

        const displayName = `Anon_${machineName}`;

        console.log('[App] Guest mode auth details:', {
          machineId,
          machineName,
          anonymousAuthId,
          displayName,
        });

        // Populate authState with hashed anonymous authId
        authState.updateState({
          accessToken: 'anon',
          hostAuthId: anonymousAuthId, // Use hashed authId, not "anon"
        });

        // Populate loginProfileState with machine-specific name and anonymous authId
        loginProfileState.updateState({
          accessToken: 'anon',
          hostAuthId: anonymousAuthId, // Use hashed authId, not "anon"
          name: displayName,
          email: '',
          phoneNumber: '',
          picture: '',
          firstName: 'Anon',
          lastName: machineName,
          expiresAt: Date.now() + 365 * 24 * 60 * 60 * 1000, // 1 year from now
        });

        console.log('[App] Guest mode auth initialized with display name:', displayName);

        // Update Sentry with guest mode display name
        if (Sentry.isInitialized()) {
          const electronStores = getElectronStores();
          const machineId = electronStores?.machineIdState?.currentState || 'unknown';

          Sentry.setUser({
            id: machineId || 'unknown',
            username: displayName,
            // Mark as guest mode for easy filtering in Sentry
            guest_mode: true,
            ip_address: '{{auto}}',
          });

          console.log('[App] Sentry updated with guest mode display name:', displayName);

          Sentry.captureMessage('Guest mode auth initialized', {
            level: 'info',
            tags: {
              guest_mode: true,
            },
            extra: {
              displayName,
              machineId,
            },
          });
        }
      };

      initializeGuestAuth();
    }
  }, []);

  useEffect(() => {
    setIsSteamInstall(steamInstallState.currentState.steamInstall);
    const listener = steamInstallState.addListener((state) => {
      setIsSteamInstall(state.steamInstall);
    });

    return () => {
      steamInstallState.removeListener(listener);
    };
  }, []);

  // Start matchmaking diagnostics monitoring in pavilion/guest mode
  useEffect(() => {
    const isAdMode = adModeState.currentState.enabled;
    const isPavilion = hostState.currentState.edition === 'pavilion';

    // Start diagnostics monitoring for ALL pavilion mode (guest mode), not just ad mode
    if (isPavilion) {
      const modeLabel = isAdMode ? 'ad mode + pavilion' : 'pavilion/guest mode';
      console.log(`[App] Starting matchmaking diagnostics monitoring (${modeLabel})`);
      const cleanup = startMatchmakingDiagnosticsMonitoring();

      // Expose manual diagnostic functions to window for easy debugging
      (window as any).dumpMatchmaking = () => sendMatchmakingDiagnostics('manual_window_call');
      (window as any).burstMatchmaking = (seconds?: number) => burstMatchmakingDiagnostics('manual_burst', seconds);
      console.log('[App] You can call window.dumpMatchmaking() to manually send diagnostics to Sentry');
      console.log('[App] You can call window.burstMatchmaking(20) to send diagnostics every 1s for 20s');

      return cleanup;
    }
  }, []);

  // Wire machine_id Sentry tag for all editions unconditionally
  useEffect(() => {
    return subscribeMachineIdToSentry(machineIdState);
  }, []);

  // Wire broadcast_id Sentry tag while the host is actively broadcasting.
  // Tag is set when hostPeerId becomes non-empty (after PEER_GET_ID_ANSWER)
  // and cleared when the stream stops.
  useEffect(() => {
    return subscribeBroadcastIdToSentry(hostPresenceState);
  }, []);

  // Monitor machineId state for initialization issues (runs for all instances)
  useEffect(() => {
    console.log('[App] Initializing machineId monitor');
    initializeMachineIdMonitor();
  }, []);

  // Initialize analytics identity system (runs for all instances)
  useEffect(() => {
    if(window.playcast?.isHost) {
      // Electron host mode - full identity tracking
      console.log('[App] Initializing analytics identity (Electron)');
      initializeAnalyticsIdentity();
      return () => {
        console.log('[App] Cleaning up analytics identity');
        cleanupAnalyticsIdentity();
      };
    } else {
      // Remote dashboard mode - limited identity tracking
      console.log('[App] Initializing analytics identity (Remote)');
      initializeAnalyticsIdentityRemote();
      return () => {
        console.log('[App] Cleaning up analytics identity');
        cleanupAnalyticsIdentity();
      };
    }
  }, []);

  // Send App Launched analytics event (Electron only)
  useAppLaunchedAnalytics();

  // Performance mode: Auto-enable when ad mode + pavilion, and track state changes
  useEffect(() => {
    // Check initial state
    const checkAndEnablePerformanceMode = () => {
      const isAdMode = adModeState.currentState.enabled;
      const isPavilion = hostState.currentState.edition === 'pavilion';

      if (isAdMode && isPavilion && !performanceModeState.currentState.enabled) {
        console.log('[Performance Mode] Auto-enabling for ad mode + pavilion (not persisting)');
        // Auto-enable but don't persist to localStorage
        performanceModeState.updateState({ enabled: true });
      }
    };

    checkAndEnablePerformanceMode();

    // Set up listeners to auto-enable if conditions change
    const adModeListener = adModeState.addListener(() => {
      checkAndEnablePerformanceMode();
    });

    const hostStateListener = hostState.addListener(() => {
      checkAndEnablePerformanceMode();
    });

    // Track performance mode state for rendering
    setIsPerformanceMode(performanceModeState.currentState.enabled);
    const performanceListener = performanceModeState.addListener((state) => {
      setIsPerformanceMode(state.enabled);
    });

    return () => {
      adModeState.removeListener(adModeListener);
      hostState.removeListener(hostStateListener);
      performanceModeState.removeListener(performanceListener);
    };
  }, []);

  useEffect(() => {
    const initializeFunctions = async () => {
      try {
        const newFunctions = await getFunctions();
        setFunctions(newFunctions);
      } catch (error) {
        console.error('Error initializing functions:', error);
      } finally {
        setIsLoading(false);
      }
    };

    initializeFunctions();
  }, []);

  useEffect(() => {
    if (!functions) return;

    let cachedHostId = '';
    const listener = remoteHostList.addListener(async (state) => {
      if (cachedHostId !== state.selectedHostId) {
        console.log('RemoteHostList updated', state);
        cachedHostId = state.selectedHostId;
        try {
          const newFunctions = await getFunctions();
          setFunctions(newFunctions);
        } catch (error) {
          console.error('Error updating functions:', error);
        }
      }
    });

    return () => {
      remoteHostList.removeListener(listener);
    };
  }, [functions]);

  useEffect(() => {
    // Skip Clerk user tracking in guest mode
    if (isGuestMode) {
      return;
    }

    if (user) {
      const username = user?.username ?? user?.fullName ?? undefined;
      const email = user?.primaryEmailAddress?.emailAddress;
      const id = user?.id;
      if (email && id) {
        if (Sentry.isInitialized()) {
          // Use helper function that preserves machineId as primary user ID
          updateSentryUser({ username, email, id });
          console.log('Sentry.setUser (with machineId)');
        }
      }
    }
  }, [user, isGuestMode]);

  // Drive ownerAuthIdState from Clerk's definitive auth state.
  // This is the single source of truth for the node-service's nonsole gating.
  // We only emit after Clerk has fully loaded so the initial empty state
  // (before Clerk resolves) does not get replicated as a false "logout".
  // The value is also persisted to localStorage so the NEXT startup can
  // pre-populate the store (avoiding a brief empty→set race on connect).
  useEffect(() => {
    if (isGuestMode || !isLoaded) {
      return;
    }
    const newOwnerAuthId = isSignedIn && userId ? userId : '';
    if (ownerAuthIdState.currentState.ownerAuthId !== newOwnerAuthId) {
      console.log('[Auth] Updating ownerAuthIdState:', newOwnerAuthId ? '(set)' : '(cleared)');
      ownerAuthIdState.updateState({ ownerAuthId: newOwnerAuthId });
    }
    if (newOwnerAuthId) {
      localStorage.setItem(OWNER_AUTH_ID_STORAGE_KEY, newOwnerAuthId);
    } else {
      localStorage.removeItem(OWNER_AUTH_ID_STORAGE_KEY);
    }
  }, [isLoaded, isSignedIn, userId, isGuestMode]);

  useEffect(() => {
    if (isGuestMode || !window.playcast || !signInLoaded || !signIn) {
      return;
    }

    const completeTicketSignIn = async (token: string) => {
      console.log('[Auth] Received auth token from Electron');
      if (isSignedInRef.current) {
        console.log('[Auth] Already signed in, ignoring token');
        return;
      }

      Sentry.captureMessage('Sign-in attempt from window.playcast', {
        level: 'info',
        tags: {
          signInSource: 'window.playcast',
          signInStrategy: 'ticket',
          attemptType: 'attempt',
        },
        contexts: {
          signIn: {
            hasToken: !!token,
            tokenLength: token?.length || 0,
            hasSignInObject: !!signIn,
          },
        },
      });

      const resource = await signIn.create({ ticket: token, strategy: 'ticket' });
      if (resource.status !== 'complete' || !resource.createdSessionId) {
        throw new Error(`Clerk ticket sign-in incomplete: ${resource.status}`);
      }

      await setActive({ session: resource.createdSessionId });

      Sentry.captureMessage('Sign-in successful from window.playcast', {
        level: 'info',
        tags: {
          signInSource: 'window.playcast',
          signInStrategy: 'ticket',
          attemptType: 'success',
        },
        contexts: {
          signIn: {
            hasToken: !!token,
            tokenLength: token?.length || 0,
            resourceStatus: resource.status,
          },
        },
      });
    };

    window.playcast.onAuthTokenReceived((token) => {
      void completeTicketSignIn(token).catch((error) => {
        console.error('[Auth] Ticket sign-in failed:', error);
        Sentry.captureException(error, {
          tags: {
            signInSource: 'window.playcast',
            signInStrategy: 'ticket',
            attemptType: 'failure',
          },
        });
      });
    });

    return () => {
      window.playcast?.removeAuthTokenListener();
    };
  }, [isGuestMode, signIn, signInLoaded, setActive]);

  // interval to get the twitch username from the user object
  useEffect(() => {
    // Skip in guest mode - no Twitch/Discord integration
    if (isGuestMode) {
      return;
    }

    if (isSignedIn && isLoaded && user) {
      const updateNames = () => {
        let twitchName = '';
        let discordName = '';
        if (user.externalAccounts) {
          const twitchAccount = user.externalAccounts.find((account) => account.provider === 'twitch');
          if (twitchAccount?.username) {
            twitchName = twitchAccount.username.charAt(0).toUpperCase() + twitchAccount.username.slice(1);
          }
          const discordAccount = user.externalAccounts.find((account) => account.provider === 'discord');
          if (discordAccount?.username) {
            discordName = discordAccount.username;
          }
        }
        if (functions?.electronFunctions) {
          functions.targetFunctions.updateTwitchSettings({
            ...functions.stateStores.twitchSettingsState.currentState,
            twitchDisplayName: twitchName,
          });
          functions.targetFunctions.updateDiscordSettings({
            ...functions.stateStores.discordSettingsState.currentState,
            discordDisplayName: discordName,
          });
        }
      };

      updateNames();
      const interval = setInterval(updateNames, 5000);

      return () => clearInterval(interval);
    }
  }, [isSignedIn, isLoaded, user, functions, isGuestMode]);

  // Separate function to update profile
  const updateProfile = useCallback(async () => {
    // Skip updating profile in guest mode - use anonymous auth instead
    if (isGuestMode) {
      console.log('[App] Skipping profile update in guest mode');
      return;
    }

    if (isSignedIn && user && isLoaded) {
      try {
        // check we are actually close to expiring
        const expTime = loginProfileState.currentState.expiresAt;
        if (expTime && expTime - Date.now() > 5 * 60 * 1000) {
          console.log('Token is not close to expiration');
          return;
        }
        // Refresh the session to ensure we have the latest token
        // await session?.reload();

        const token = await ClerkFrontendDiagnostics.wrapClerkCall(
          'session.getToken',
          async () => await session?.getToken({ template: 'host-token' }),
          {
            userId: user.id,
            expiresAt: expTime,
            timeUntilExpiry: expTime ? expTime - Date.now() : null
          }
        );
        if (!token) {
          console.warn('No token received from session');
          return;
        }

        // Decode the JWT to get expiration
        const tokenParts = token.split('.');
        if (tokenParts.length === 3) {
          const payload = JSON.parse(atob(tokenParts[1]));
          const expiresAt = payload.exp * 1000; // Convert to milliseconds

          const profile: LoginProfile = {
            accessToken: token,
            name: user.fullName ?? '',
            email: user.primaryEmailAddress?.toString() ?? '',
            phoneNumber: user.primaryPhoneNumber?.toString() ?? '',
            picture: user.imageUrl ?? '',
            firstName: user.firstName ?? '',
            lastName: user.lastName ?? '',
            hostAuthId: user.id ?? '',
            expiresAt: expiresAt,
          };
          console.log('Expires at:', new Date(expiresAt).toLocaleString());
          loginProfileState.updateState(profile);
        }
      } catch (error) {
        console.error('Error updating profile:', error);
        // Error is already tracked by ClerkFrontendDiagnostics
      }
    }
  }, [isSignedIn, user, isLoaded, session]);

  // Effect to handle token refresh
  useEffect(() => {
    if (isSignedIn && user && isLoaded) {
      // Initial update
      console.log('Updating profile, initial');
      updateProfile();

      // Set up interval to check token expiration
      const interval = setInterval(async () => {
        const currentTime = Date.now();
        const tokenExpiresAt = loginProfileState.currentState.expiresAt;

        // If token is expired or about to expire in the next 1 minute
        if (tokenExpiresAt && currentTime > tokenExpiresAt - 1 * 60 * 1000) {
          console.log('Token needs refresh');
          await updateProfile();
        }
      }, 60 * 100); // Check every minute

      setTokenRefreshInterval(interval);

      return () => {
        if (tokenRefreshInterval) {
          clearInterval(tokenRefreshInterval);
        }
      };
    }
  }, [isLoaded, isSignedIn, user, session]);

  useEffect(() => {
    if (functions?.electronFunctions) {
      functions.targetFunctions.getAutomaticUpdatesEnabled();
    }
  }, [functions]);

  // Wrap everything in MachineIdGuard to validate machineId before app initialization
  return (
    <MachineIdGuard>
      {(() => {
        if (!isLoaded) {
          return (
            <div className='flex h-screen items-center justify-center' style={{ background: '#3D2A69' }}>
              <div className='relative box-border flex h-full min-h-0 w-full flex-col overflow-hidden rounded-[12px] border-[3px] border-[#3D2A69] bg-[#0b0618]'>
                <button type='button' onClick={() => window.close()}
                  className='absolute right-[14px] top-[14px] z-10 flex items-center justify-center transition-opacity hover:opacity-80'
                  style={{ width: 22, height: 22, lineHeight: 0 }}
                  aria-label='Close'>
                  <svg width='22' height='22' viewBox='0 0 24 24' aria-hidden='true'>
                    <path d='M6.4 6.4 L17.6 17.6 M17.6 6.4 L6.4 17.6' fill='none' stroke='rgba(255,255,255,0.8)' strokeWidth='2' strokeLinecap='round' />
                  </svg>
                </button>
                <div className='flex min-h-0 flex-1 flex-col items-center justify-center'>
                  <img src='/playcast-gibber.svg' alt='Playcast' className='w-[139px] h-[112px] shrink-0 object-contain' />
                  <h1 className='shrink-0 text-[32px] font-bold tracking-tight text-[#FBBF24]' style={{ fontFamily: 'Inter, sans-serif' }}>Playcast</h1>
                  <div className='mt-8 shrink-0 rounded-full bg-[#FBBF24]/60 px-12 py-2.5 flex items-center justify-center'>
                    <div className='h-6 w-6 animate-spin rounded-full border-[3px] border-[#020617]/20 border-t-[#020617]' />
                  </div>
                </div>
              </div>
            </div>
          );
        }

        if (!isElectron && !isSignedIn) {
          return (
            <div className='flex h-screen items-center justify-center'>
              <SignIn />
            </div>
          );
        }

        if (!isElectron && window.location.search.includes('preview=rewards')) {
          return (
            <div className='flex h-screen items-center justify-center bg-neutral-900'>
              <div style={{ width: 300, height: 560 }} className='flex overflow-hidden'>
                <PavilionPostLogin />
              </div>
            </div>
          );
        }

        if (!functions) {
          return (
            <div className='flex h-screen items-center justify-center'>
              <div>Error loading application functions</div>
            </div>
          );
        }

        // Performance mode: Show minimal UI with only a disable button
        if (isPerformanceMode) {
          return (
            <div className='flex h-screen items-center justify-center bg-black'>
              <div className='flex flex-col items-center gap-y-6 text-white'>
                <div className='text-center'>
                  <h1 className='text-4xl font-bold'>Performance Mode Active</h1>
                  <p className='mt-2 text-gray-400'>UI disabled for optimal performance</p>
                </div>
                <button
                  onClick={() => {
                    console.log('[Performance Mode] Manually disabled');
                    performanceModeState.updateState({ enabled: false });
                    localStorage.setItem('performanceMode', 'false');
                  }}
                  className='rounded-lg bg-red-600 px-6 py-3 font-semibold hover:bg-red-700'
                >
                  Disable Performance Mode
                </button>
              </div>
            </div>
          );
        }

        if (isBeaconMode) {
          return (
            <PlaycastWrapper functions={functions}>
              <DevData />
              <ReleaseDevData />
              <NotificationToastWrapper />
              <BeaconModeComponent />
            </PlaycastWrapper>
          );
        }

        if (isSteamInstall) {
          return (
            <PlaycastWrapper functions={functions}>
              <DevData />
              <ReleaseDevData />
              <NotificationToastWrapper />
              <SteamModeComponent />
            </PlaycastWrapper>
          );
        }
        return (
          <PlaycastWrapper functions={functions}>
            <DevData />
            <ReleaseDevData />
            <NotificationToastWrapper />
            <FunctionConnectionComponent />
          </PlaycastWrapper>
        );
      })()}
    </MachineIdGuard>
  );
}

const BeaconModeComponent = () => {
  const Router = isElectron ? HashRouter : MemoryRouter;
  const context = useContext(PlaycastContext);

  if (!context) {
    return (
      <div className='flex h-96 items-center justify-center text-white'>
        <div className='flex flex-col items-center gap-y-4'>
          {!isElectron && (
            <>
              <FontAwesomeIcon icon={faFaceThinking} className='text-primary text-9xl' />
              <h1 className='text-3xl'>Initializing Beacon...</h1>
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <Router>
      <Chrome>
        <Routes>
          <Route path='/' element={<Navigate to='/studio' replace />} />
          <Route path='/studio' element={<BeaconStudio />} />
          <Route path='/playcast' element={<BeaconPlayerControls />} />
        </Routes>
      </Chrome>
      <Tooltip id='tooltip' />
      <Toaster richColors toastOptions={{}} position='top-right' />
      <ToastContainer position='top-right' />
    </Router>
  );
};

const SteamModeComponent = () => {
  const Router = isElectron ? HashRouter : MemoryRouter;
  const context = useContext(PlaycastContext);
  const { isSignedIn } = useAuth();
  const [hasAcceptedOnboarding, setHasAcceptedOnboarding] = useState(false);

  if (!context) {
    if (isElectron) {
      return (
        <div className='flex h-96 items-center justify-center text-white'>
          <div className='flex flex-col items-center gap-y-4'></div>
        </div>
      );
    }
    return (
      <div className='flex h-96 items-center justify-center text-white'>
        <div className='flex flex-col items-center gap-y-4'>
          <FontAwesomeIcon icon={faFaceThinking} className='text-primary text-9xl' />
          <h1 className='text-3xl'>Initializing Playcast...</h1>
        </div>
      </div>
    );
  }

  const handleAcceptOnboarding = () => setHasAcceptedOnboarding(true);

  const steamShell = isSignedIn && hasAcceptedOnboarding ? (
    <Chrome noDefaultNavbar>
      <Steam hasAcceptedOnboarding={hasAcceptedOnboarding} onAcceptOnboarding={handleAcceptOnboarding} />
    </Chrome>
  ) : (
    <div className='flex h-screen min-h-0 w-full flex-col overflow-hidden bg-transparent'>
      <Steam hasAcceptedOnboarding={hasAcceptedOnboarding} onAcceptOnboarding={handleAcceptOnboarding} />
    </div>
  );

  return (
    <Router>
      <UpdaterModal />
      {steamShell}
      <Tooltip id='tooltip' />
      <Toaster richColors toastOptions={{}} position='top-right' />
      <ToastContainer position='top-right' />
    </Router>
  );
};

const FunctionConnectionComponent = () => {
  const Router = isElectron ? HashRouter : MemoryRouter;

  const context = useContext(PlaycastContext);

  const [hosts, setHosts] = useState<{ value: string; label: string; icon: IconProp; enabled: boolean }[]>([]);
  const [selectedHost, setSelectedHost] = useState<string>('');
  const [ready, setReady] = useState<boolean>(
    context.stateStores.functionConnectionState.functionKey === '' && context.electronFunctions !== undefined,
  );
  const [initializing, setInitializing] = useState(true);
  const [connectionError, setConnectionError] = useState(false);
  const [lastKnownHost, setLastKnownHost] = useState('');

  // Use a ref to track the previous result
  const prevResultRef = useRef<string | null>(null);

  useEffect(() => {
    const hostList = context.stateStores.remoteHostList;
    const maybeLastKnown = hostList.hosts[hostList.selectedHostId]?.displayName || '';
    if (maybeLastKnown) {
      setLastKnownHost(maybeLastKnown);
    }
    const newHosts = Object.values(hostList.hosts).map((host) => {
      return {
        value: host.remoteId,
        label: host.displayName,
        icon: faComputer,
        enabled: true,
      };
    });
    const activeHosts = newHosts.length;
    const inactiveHosts = Object.values(hostList.inactive).map((host) => {
      return {
        value: host.remoteId,
        label: host.displayName,
        icon: faCancel,
        enabled: false,
      };
    });
    newHosts.push(...inactiveHosts);
    setHosts(newHosts);
    if (activeHosts === 0) {
      setSelectedHost('');
    } else {
      setSelectedHost(hostList.selectedHostId);
    }
    handleHostChanged(hostList.selectedHostId);
  }, [context.stateStores.remoteHostList]);

  useEffect(() => {
    if (!context) return;

    const connectionState = context.stateStores.functionConnectionState;

    if (connectionState.functionKey) {
      setReady(connectionState.connected);
    }

    if (connectionState.error) {
      setReady(false);
      setConnectionError(true);
    }

    setInitializing(connectionState.initializing);
  }, [
    context,
    context.stateStores.functionConnectionState.connected,
    context.stateStores.functionConnectionState.error,
    context.stateStores.functionConnectionState.initializing,
  ]);

  useEffect(() => {
    if (!context) {
      setInitializing(true);
    } else {
      setInitializing(context.stateStores.functionConnectionState.initializing);
    }
  }, [context]);

  useEffect(() => {
    const currentResult = context.stateStores.gameClipStatusState.result;

    // Only show toast if the result has changed to 'finished' or 'started'
    if (currentResult === 'finished' && prevResultRef.current !== 'finished') {
      // Type assertion to access filePath which is only available when result is 'finished'
      const status = context.stateStores.gameClipStatusState as { result: 'finished'; filePath: string };
      showToast({
        message: 'Playcast Clip Captured',
        subMessage: `at '${status.filePath}'`,
        icon: faCheck,
        iconBgColor: 'bg-green-400',
        duration: 3000,
      });
    }

    if (currentResult === 'started' && prevResultRef.current !== 'started') {
      showToast({
        message: 'Playcast Clip Started',
        icon: faSpinner,
        iconBgColor: 'bg-amber-400',
        iconClassName: 'animate-spin',
        duration: 3000,
      });
    }

    // Update the ref with the current result
    prevResultRef.current = currentResult;
  }, [context.stateStores.gameClipStatusState]);

  useEffect(() => {
    if (!context.electronFunctions) return;

    const gameClipConfig = context.stateStores.gameClipConfigState;
    const timeoutId = setTimeout(() => {
      context.targetFunctions?.setEncoderConfig(gameClipConfig);
    }, 2500);

    // Cleanup timeout if component unmounts
    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleHostChanged = (value: string) => {
    if (value !== '') {
      const host = hosts.find((host) => host.value === value);
      if (host === undefined) {
        return;
      } else {
        context.changeFunctionTarget(value);
      }
    }
  };

  if (!context) {
    if (isElectron) {
      return (
        <div className='flex h-96 items-center justify-center text-white'>
          <div className='flex flex-col items-center gap-y-4'></div>
        </div>
      );
    }
    return (
      <div className='flex h-96 items-center justify-center text-white'>
        <div className='flex flex-col items-center gap-y-4'>
          <FontAwesomeIcon icon={faFaceThinking} className='text-primary text-9xl' />
          <h1 className='text-3xl'>Initializing Playcast...</h1>
        </div>
      </div>
    );
  }

  return (
    <Router>
      <Chrome>
        {!context.electronFunctions &&
        context.stateStores.authState.accessToken &&
        hosts.find((host) => host.value === selectedHost || context.electronFunctions === undefined)
          ? null
          : null}
        {initializing ? (
          <div className='flex h-96 items-center justify-center text-white'>
            <div className='flex flex-col items-center gap-y-4'>
              {!isElectron && (
                <>
                  <FontAwesomeIcon icon={faFaceThinking} className='text-primary text-9xl' />
                  <h1 className='text-3xl'>Initializing...</h1>
                </>
              )}
            </div>
          </div>
        ) : ready ? (
          <Routes>
            <Route path='/' element={<Content />}>
              <Route index element={<Navigate to='/studio' />} />
              <Route path='test' element={<TesterTools />} />
              <Route path='about' element={<About />} />
              <Route path='developer' element={<DevData />} />
              <Route path='experimental' element={<Experimental />} />

              <Route path='studio' element={USE_BEACON_COORDINATOR ? <BeaconStudio /> : <Studio />} />
              {/* <Route path='playcast' element={<Playcast />} /> */}
              {/* <Route path='lobby' element={<Lobby />} /> */}
              <Route path='playcast' element={<LobbyV2 />} />
              <Route path='inputs' element={<Inputs />} />
            </Route>
          </Routes>
        ) : connectionError ? (
          <div className='flex h-96 items-center justify-center text-white'>
            <div className='flex flex-col items-center gap-y-4'>
              <FontAwesomeIcon icon={faWifiSlash} className='text-9xl text-red-600' />
              <h1 className='text-3xl'>Couldn't connect to {lastKnownHost}</h1>
            </div>
          </div>
        ) : hosts.filter((host) => host.enabled).length === 0 ? (
          <div className='relative flex flex-1 flex-col items-center gap-4 p-8 text-center'>
            <h2 className='text-center text-2xl'>No Playcast Server Found</h2>
            <span>Login to your Playcast app on your PC to control it from here</span>
            <span>or</span>
            <span>Download the Playcast app to start Playcasting</span>
            <Link
              className={clsx(buttonVariants(), '!embossed-primary z-10 m-0 w-fit text-sm font-semibold')}
              to='https://playcast.io'
              target='_blank'
            >
              Download
            </Link>
            <img src={playcastHolo} alt='Logo' className='absolute size-full select-none object-none opacity-40 mix-blend-lighten' />
          </div>
        ) : (
          <div className='flex h-96 items-center justify-center text-white'>
            <div className='flex flex-col items-center gap-y-4'>
              <FontAwesomeIcon icon={faWifi} className='text-primary text-9xl' />
              <h1 className='text-3xl'>Connecting...</h1>
            </div>
          </div>
        )}
      </Chrome>
      <Tooltip id='tooltip' />
      <Toaster richColors toastOptions={{}} position='top-right' />
      <ToastContainer position='top-right' />
    </Router>
  );
};
